import type { HTMLAttributes, PropsWithChildren } from 'react'

import { useTranslation } from 'react-i18next'

import { Icon } from '@/components/icon'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card, CardHeader } from '@/components/ui/card'
import { cn } from '@/infra'
import { getIniciaisPrimeiroUltimoNome, StringUtils } from '@/utils'

const CardComprador = ({ children }: PropsWithChildren) => {
  return <Card className="shadow-cz br-4 w-full rounded-none border-none p-4">{children}</Card>
}

const CardCompradorHeader = ({ onDelete, children }: PropsWithChildren<{ onDelete?: () => void }>) => {
  const { t } = useTranslation('proposta')
  return (
    <CardHeader className="flex-row items-center justify-between rounded-sm bg-neutral-100 px-2 py-0">
      {children}
      <Button type="button" variant="ghost" onClick={onDelete} className="text-sky-700">
        <Icon name="trash-2" />
        {t('cadastro_compradores.excluir')}
      </Button>
    </CardHeader>
  )
}

interface ICardCompradorContentProps extends HTMLAttributes<HTMLButtonElement> {
  onEdit?: () => void
}

const CardCompradorContent = ({
  children,
  onEdit,
  className,
  ...props
}: PropsWithChildren<ICardCompradorContentProps>) => {
  return (
    <button
      onClick={onEdit}
      className={cn(
        'hover:bg-muted/50 flex w-full cursor-pointer items-center justify-between border-gray-100 py-4 text-start transition-colors not-last:border-b',
        className,
      )}
      {...props}
    >
      <div className="flex gap-4">{children}</div>
      <Icon name="chevron-right" className="h-6 w-6 text-neutral-800" />
    </button>
  )
}

interface ICardCompradorInfoProps {
  tipo: string
  nome: string
  imagem?: string | null
  exibirAvatar?: boolean
}
const CardCompradorInfo = ({ tipo, nome, imagem, exibirAvatar = true }: ICardCompradorInfoProps) => {
  return (
    <>
      <Avatar className="size-2 h-8 w-8 text-white">
        <AvatarImage src={imagem ?? undefined} alt={`imagem do ${tipo}`} />
        <AvatarFallback className={exibirAvatar ? 'bg-red-900 text-xs' : 'invisible size-8 select-none'}>
          {getIniciaisPrimeiroUltimoNome(nome)}
        </AvatarFallback>
      </Avatar>
      {!exibirAvatar && <Icon name="corner-down-right" />}
      <div>
        <p className="mb-1 text-sm font-semibold text-neutral-500">{tipo}</p>
        <p className="font-normal text-neutral-800">{StringUtils.capitalize(nome)}</p>
      </div>
    </>
  )
}

export { CardComprador, CardCompradorContent, CardCompradorHeader, CardCompradorInfo }
